import { SHOPIFY_API_KEY } from "../services/CONSTANTS";
import { getEmbeddedShopDomain } from "./embeddedAppUtils";

/*
 * Liquid filename of the IFH Display Arrival Dates app block
 * (extensions/ifh-display-arrival-dates/blocks/arrival-date.liquid).
 * Deep links use this handle, not the extension handle.
 */
export const DISPLAY_ARRIVAL_DATES_BLOCK_HANDLE = "arrival-date";

// Utilities to generate Shopify Admin URLs for product and variant pages

/**
 * Extracts the shop name from a Shopify domain
 * Example: "narens-shop.myshopify.com" -> return "narens-shop"
 */
export function extractShopNameFromDomain(shopDomain) {
    const [shop] = String(shopDomain).trim().split(".");
    return shop
}

/**
 * App handle from build env, or the API key Shopify also accepts in /apps/.
 * Example: "inventory-forecast-hero-dev"
 */
function getEmbeddedAppSlug() {
    return process.env.REACT_APP_SHOPIFY_APP_HANDLE || SHOPIFY_API_KEY || "";
}

function normalizeEmbeddedAppPath(appPath) {
    if (!appPath) {
        return "";
    }
    return appPath.startsWith("/") ? appPath : `/${appPath}`;
}

/**
 * App Bridge protocol URL for an embedded app route.
 * App Bridge opens this as a Shopify Admin tab, not a standalone app origin tab.
 *
 * Example:
 *   getShopifyProtocolEmbeddedAppUrl("/ui/chatbot")
 *   → "shopify://admin/apps/inventory-forecast-hero-dev/ui/chatbot"
 */
export function getShopifyProtocolEmbeddedAppUrl(appPath) {
    const appSlug = getEmbeddedAppSlug();
    const path = normalizeEmbeddedAppPath(appPath);
    if (!appSlug || !path) {
        return null;
    }
    return `shopify://admin/apps/${appSlug}${path}`;
}

/**
 * Full Admin URL for an embedded app route (the URL merchants can paste).
 *
 * Example:
 *   getShopifyAdminEmbeddedAppUrl("saeel-test-store.myshopify.com", "/ui/chatbot")
 *   → "https://admin.shopify.com/store/saeel-test-store/apps/inventory-forecast-hero-dev/ui/chatbot"
 */
export function getShopifyAdminEmbeddedAppUrl(shopDomain, appPath) {
    const store = extractShopNameFromDomain(shopDomain || "");
    const appSlug = getEmbeddedAppSlug();
    const path = normalizeEmbeddedAppPath(appPath);
    if (!store || !appSlug || !path) {
        return null;
    }
    return `https://admin.shopify.com/store/${store}/apps/${appSlug}${path}`;
}

/**
 * Opens an embedded app path in a new Shopify Admin tab.
 * Relative window.open("/ui/chatbot", "_blank") loads the SPA origin as a
 * top-level tab (standalone notice). Admin / shopify: URLs keep the iframe.
 *
 * Example: openEmbeddedAppPathInNewAdminTab("/ui/chatbot")
 */
export function openEmbeddedAppPathInNewAdminTab(appPath) {
    const adminUrl = getShopifyAdminEmbeddedAppUrl(
        getEmbeddedShopDomain(),
        appPath
    );
    if (adminUrl) {
        const opened = window.open(adminUrl, "_blank");
        if (opened) {
            return true;
        }
    }

    /*
     * If the shop domain is missing, App Bridge can still resolve shopify:
     * URLs in the current Admin session.
     */
    const protocolUrl = getShopifyProtocolEmbeddedAppUrl(appPath);
    if (!protocolUrl) {
        return false;
    }

    return Boolean(window.open(protocolUrl, "_blank"));
}

/*
 * App handle in the Shopify admin uninstall page.
 * Production, staging, and build-local already set REACT_APP_SHOPIFY_APP_HANDLE.
 * npm start does not, so local falls back to local-extension-test.
 *
 * Example (local):  "local-extension-test"
 * Example (staging): "forecast-allteam-ecs-staging"
 * Example (production): "inventory-forecasting-hero"
 */
function getAppInstallationHandle() {
    return process.env.REACT_APP_SHOPIFY_APP_HANDLE || "local-extension-test";
}

/**
 * Installed-app page in Shopify admin, where the Uninstall action lives.
 * Store handle comes from the shop domain. App handle follows the build environment.
 *
 * Example:
 *   getShopifyAppInstallationUrl("narens-shop.myshopify.com")
 *   → "https://admin.shopify.com/store/narens-shop/settings/apps/app_installations/app/local-extension-test"
 */
export function getShopifyAppInstallationUrl(shopDomain) {
    const store = extractShopNameFromDomain(shopDomain || getEmbeddedShopDomain() || "");
    if (!store) {
        return "";
    }
    return `https://admin.shopify.com/store/${store}/settings/apps/app_installations/app/${getAppInstallationHandle()}`;
}

/**
 * Builds a Shopify Admin product URL.
 * Example: https://admin.shopify.com/store/{shop}/products/{productId}
 */
export function getShopifyAdminProductUrl(shopDomain, productId) {
    const store = extractShopNameFromDomain(shopDomain);
    if (!store || !productId) return "";
    return `https://admin.shopify.com/store/${store}/products/${productId}`;
}

/**
 * Builds a Shopify Admin variant URL.
 * Example: https://admin.shopify.com/store/{shop}/products/{productId}/variants/{variantId}
 */
export function getShopifyAdminVariantUrl(shopDomain, productId, variantId) {
    const store = extractShopNameFromDomain(shopDomain);
    if (!store || !productId || !variantId) return "";
    return `https://admin.shopify.com/store/${store}/products/${productId}/variants/${variantId}`;
}

/**
 * Theme editor deep link that adds IFH Display Arrival Dates to the
 * product page main section. Merchant previews the block, then saves.
 *
 * Shopify docs:
 * https://shopify.dev/docs/apps/build/online-store/theme-app-extensions/configuration#deep-linking
 *
 * Example:
 *   getDisplayArrivalDatesThemeEditorUrl("narens-shop.myshopify.com")
 *   → "https://admin.shopify.com/store/narens-shop/themes/current/editor?template=product&addAppBlockId=2b0686c5272efa407f7a2c394bf0d6d0/arrival-date&target=mainSection"
 */
export function getDisplayArrivalDatesThemeEditorUrl(shopDomain) {
    const store = extractShopNameFromDomain(shopDomain || getEmbeddedShopDomain() || "");
    const apiKey = SHOPIFY_API_KEY;
    const handle = DISPLAY_ARRIVAL_DATES_BLOCK_HANDLE;
    if (!store || !apiKey || !handle) {
        return "";
    }
    return `https://admin.shopify.com/store/${store}/themes/current/editor?template=product&addAppBlockId=${apiKey}/${handle}&target=mainSection`;
}